import React, { useState } from 'react';
import {
  Typography,
  Stack,
  Button,
  TextField,
  Container,
  Link,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import HeroBannerImage from '../assets/images/banner5.jpg';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, push, set, get } from 'firebase/database';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';

const TrainerRegistration = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [age, setAge] = useState('');
  const [experience, setExperience] = useState('');
  const [knowledge, setKnowledge] = useState('');
  const [hours, setHours] = useState('');
  const [contactNumber, setContactNumber] = useState(''); 
  const [monthlyCharge, setMonthlyCharge] = useState(''); 
  const [error, setError] = useState('');
  const [registered, setRegistered] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Perform field validations
    if (name.trim() === '') {
      setError('Name is required');
      return;
    }

    if (email.trim() === '') {
      setError('Email is required');
      return;
    }

    if (password.trim() === '') {
      setError('Password is required');
      return;
    }

    if (confirmPassword.trim() === '') {
      setError('Confirm Password is required');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (age.trim() === '' || parseInt(age) < 0) {
      setError('Age is required');
      return;
    }

    if (experience.trim() === '' || parseInt(experience) < 0) {
      setError('Previous Experience is required');
      return;
    }

    if (knowledge.trim() === '') {
      setError('Knowledge of Basic Fitness Programs is required');
      return;
    }

    if (hours.trim() === '' || parseInt(hours) < 0) {
      setError('Number of Hours Trainer Can Work is required');
      return;
    }

    if (contactNumber.trim() === '' || parseInt(contactNumber) < 0 || parseInt(contactNumber) < 10) { // Added contact number validation
      setError('Contact Number is required');
      return;
    }

    if (monthlyCharge.trim() === '' || parseInt(monthlyCharge) < 0) { // Added contact number validation
      setError('Monthly Charges is required');
      return;
    }

    // Check if email already exists in the database
    const db = getDatabase();
    const trainersRef = ref(db, 'trainers');
    const snapshot = await get(trainersRef);

    if (snapshot.exists()) {
      const trainers = Object.values(snapshot.val());
      const existingTrainer = trainers.find((trainer) => trainer.email === email);
      if (existingTrainer) {
        setError('Email already exists. Please choose a different email.');
        return;
      }
    }

    // If email doesn't exist, proceed with registration
    const newTrainer = {
      name,
      email,
      age,
      experience,
      knowledge,
      hours,
      contactNumber,
      monthlyCharge,
    };

    const auth = getAuth();
    createUserWithEmailAndPassword(auth, email, password)
      .then(async (userCredential) => {
        // Signed in successfully
        const user = userCredential.user;
        // Store the trainer in the database
        try {
          const newRef = push(trainersRef);
          await set(newRef, newTrainer);
          setRegistered(true);
          navigate('/login');
        } catch (error) {
          setError('Failed to store trainer details. Please try again.');
        }
      })
      .catch((error) => {
        const errorMessage = error.message;
        setError(errorMessage);
      });
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prevShowConfirmPassword) => !prevShowConfirmPassword);
  };

  return (
    <Container maxWidth="md">
      <Stack gap={5} sx={{ p: '20px' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" gap={3}>
          <img
            src={HeroBannerImage}
            alt="Exercise Detail"
            loading="lazy"
            style={{ maxWidth: '65%', height: 'auto', borderRadius: '20px', marginRight: '20px', marginLeft: '-20px' }}
          />

          <Stack sx={{ gap: { xs: '20px', md: '35px' } }} maxWidth="100%">
            <Typography variant="h3" textAlign="center" mt={10}>
              Trainer Registration
            </Typography>

            <form onSubmit={handleSubmit}>
              <Stack direction="column" gap={3}>
                <TextField
                  label="Name"
                  variant="outlined"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={error && error.includes('Name')}
                  helperText={error && error.includes('Name') && error}
                  required
                  fullWidth
                />

                <TextField
                  type="email"
                  label="Email"
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={error && error.includes('Email')}
                  helperText={error && error.includes('Email') && error}
                  required
                  fullWidth
                />

                <TextField
                  type={showPassword ? 'text' : 'password'}
                  label="Password"
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={error && error.includes('Password')}
                  helperText={error && error.includes('Password') && error}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={togglePasswordVisibility} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  required
                  fullWidth
                />

                <TextField
                  type={showConfirmPassword ? 'text' : 'password'}
                  label="Confirm Password"
                  variant="outlined"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={error && error.includes('Confirm Password')}
                  helperText={error && error.includes('Confirm Password') && error}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={toggleConfirmPasswordVisibility} edge="end">
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  required
                  fullWidth
                />

                <TextField
                  type="number"
                  label="Age"
                  variant="outlined"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  error={error && error.includes('Age')}
                  helperText={error && error.includes('Age') && error}
                  required
                  fullWidth
                />

                <TextField
                  type="number"
                  label="Previous Experience as Trainer (in years)"
                  variant="outlined"
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                  error={error && error.includes('Previous Experience')}
                  helperText={error && error.includes('Previous Experience') && error}
                  required
                  fullWidth
                />

                <TextField
                  label="Knowledge of Basic Fitness Programs"
                  variant="outlined"
                  value={knowledge}
                  onChange={(e) => setKnowledge(e.target.value)}
                  error={error && error.includes('Knowledge of Basic Fitness Programs')}
                  helperText={
                    error && error.includes('Knowledge of Basic Fitness Programs') && error
                  }
                  required
                  fullWidth
                />

                <TextField
                  type="number"
                  label="Number of Hours Trainer Can Work (per week)"
                  variant="outlined"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  error={error && error.includes('Number of Hours Trainer Can Work')}
                  helperText={error && error.includes('Number of Hours Trainer Can Work') && error}
                  required
                  fullWidth
                />

                <TextField 
                  type="tel"
                  label="Contact Number"
                  variant="outlined"
                  value={contactNumber}
                  onChange={(e) => setContactNumber(e.target.value)}
                  error={error && error.includes('Contact Number')}
                  helperText={error && error.includes('Contact Number') && error}
                  required
                  fullWidth
                />

                <TextField 
                  type="tel"
                  label="Monthly Charge (in rupees)"
                  variant="outlined"
                  value={monthlyCharge}
                  onChange={(e) => setMonthlyCharge(e.target.value)}
                  error={error && error.includes('Monthly Charge')}
                  helperText={error && error.includes('Monthly Charge') && error}
                  required
                  fullWidth
                />

                <Button variant="contained" type="submit" fullWidth>
                  Register
                </Button>

                {error && !error.includes('Name') && (
                  <Typography variant="body2" color="error" align="center">
                    {error}
                  </Typography>
                )}

                {registered && (
                  <Typography variant="body2" color="success" align="center">
                    Registration successful! Please <Link href="/trainer-login">log in</Link> to continue.
                  </Typography>
                )}

                <Typography variant="body2" align="center">
                  Already have an account? <Link href="/trainer-login">Log in</Link>
                </Typography>
              </Stack>
            </form>
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
};

export default TrainerRegistration;
